import { useMemo } from "react";
import { useQuery } from "@rocicorp/zero/react";
import { useZeroSelector } from "../../hooks/use-zero-selector.js";
import { queries } from "../queries";
import { useCurrentUser } from "../current-user";
import { useLastReadMap, useMutedSet } from "./memberships";
import {
  createIncrementalMapSelector,
  createScalarSelector,
} from "../incremental-map-selector";

const CACHE_KEY = "channel-stats.byUser";

// ─── Selector factories ─────────────────────────────────────────────

function createUnreadCountMapSelector() {
  return createIncrementalMapSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (s: any) => s.channel_id as string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (s: any) => (s.unread_count > 0 ? (s.unread_count as number) : 0),
    (prev, next) => prev === next,
  );
}

function createMentionCountMapSelector() {
  return createIncrementalMapSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (s: any) => s.channel_id as string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (s: any) => (s.mention_count > 0 ? (s.mention_count as number) : 0),
    (prev, next) => prev === next,
  );
}

function createLastMessageAtMapSelector() {
  return createIncrementalMapSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (s: any) => s.channel_id as string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (s: any) => (s.last_message_at ? (s.last_message_at as number) : 0),
    (prev, next) => prev === next,
  );
}

function createUnreadCountSelector(channelId: string) {
  return createScalarSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (data: readonly any[]) => {
      const stat = data.find((s) => s.channel_id === channelId);
      return stat?.unread_count > 0 ? (stat.unread_count as number) : 0;
    },
  );
}

function createMentionCountSelector(channelId: string) {
  return createScalarSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (data: readonly any[]) => {
      const stat = data.find((s) => s.channel_id === channelId);
      return stat?.mention_count > 0 ? (stat.mention_count as number) : 0;
    },
  );
}

// ─── Module-level singleton selectors + defaults ─────────────────────
const unreadCountMapSelector = createUnreadCountMapSelector();
const mentionCountMapSelector = createMentionCountMapSelector();
const lastMessageAtMapSelector = createLastMessageAtMapSelector();
const EMPTY_COUNT_MAP = new Map<string, number>();

// Per-channel selectors need a cache (parameterized by channelId)
const unreadCountSelectorCache = new Map<
  string,
  ReturnType<typeof createUnreadCountSelector>
>();
const mentionCountSelectorCache = new Map<
  string,
  ReturnType<typeof createMentionCountSelector>
>();

function getUnreadCountSelector(channelId: string) {
  let s = unreadCountSelectorCache.get(channelId);
  if (!s) {
    s = createUnreadCountSelector(channelId);
    unreadCountSelectorCache.set(channelId, s);
  }
  return s;
}

function getMentionCountSelector(channelId: string) {
  let s = mentionCountSelectorCache.get(channelId);
  if (!s) {
    s = createMentionCountSelector(channelId);
    mentionCountSelectorCache.set(channelId, s);
  }
  return s;
}

// ─── Hooks ──────────────────────────────────────────────────────────

/** Full unread count Map. For channel sorting. */
export function useUnreadCountMap() {
  const { user } = useCurrentUser();
  const selector = unreadCountMapSelector;
  return useZeroSelector(
    CACHE_KEY,
    user ? queries.user_channel_stats.byUser() : null,
    selector,
    { defaultValue: EMPTY_COUNT_MAP },
  );
}

/** Single channel's unread count. For UnreadBadge. */
export function useUnreadCount(channelId: string) {
  const { user } = useCurrentUser();
  const selector = getUnreadCountSelector(channelId);
  return useZeroSelector(
    CACHE_KEY,
    user ? queries.user_channel_stats.byUser() : null,
    selector,
    { defaultValue: 0 },
  );
}

/** Full mention count Map. (No active consumer post cross-channel-mentions removal — kept exported for follow-up dead-code sweep.) */
export function useMentionCountMap() {
  const { user } = useCurrentUser();
  const selector = mentionCountMapSelector;
  return useZeroSelector(
    CACHE_KEY,
    user ? queries.user_channel_stats.byUser() : null,
    selector,
    { defaultValue: EMPTY_COUNT_MAP },
  );
}

/** Single channel's mention count. */
export function useMentionCount(channelId: string) {
  const { user } = useCurrentUser();
  const selector = getMentionCountSelector(channelId);
  return useZeroSelector(
    CACHE_KEY,
    user ? queries.user_channel_stats.byUser() : null,
    selector,
    { defaultValue: 0 },
  );
}

/** Full last-message-at Map. For DMList channel ordering. */
export function useLastMessageAtMap() {
  const { user } = useCurrentUser();
  const selector = lastMessageAtMapSelector;
  return useZeroSelector(
    CACHE_KEY,
    user ? queries.user_channel_stats.byUser() : null,
    selector,
    { defaultValue: EMPTY_COUNT_MAP },
  );
}

/**
 * A single message-derived inbox item, shaped for both the OS-banner /
 * sound hook (`useNotifications`) and the inbox badge count. Sourced
 * from `messages.inboxForUser` — the SAME query the inbox LIST reads —
 * so banners, sounds, the badge, and the list all agree.
 *
 * Migration 104 row-filtered the `notification_events` Zero publication
 * down to admin reasons only (role_upgraded / added_to_space /
 * added_to_channel), so DM / mention / notify=all rows never replicate
 * to the desktop client. Reading those off `notification_events` made
 * the banner loop iterate an empty set and the badge undercount; the
 * fix is to derive them from `messages` like the inbox feed already does.
 */
export interface MessageInboxItem {
  /** `messages.id` — stable identity for banner dedup + badge counting. */
  id: string;
  channelId: string;
  messageId: string;
  threadId: string | undefined;
  workspaceId: string | undefined;
  /** `"dm" | "mention" | "channel"` — drives sound choice + filter reason. */
  reason: string;
  /** Banner title: sender display name (falls back to "New message"). */
  title: string;
  /** Banner body: message preview / content. */
  body: string;
  /** Author user id — consumers skip self-authored items. */
  actorUserId: string;
  /** Message timestamp (ms). */
  createdAt: number;
}

const EMPTY_MESSAGE_INBOX_ITEMS: MessageInboxItem[] = [];

/**
 * Derive the unread, message-backed inbox items for the current user.
 *
 * Mirrors `use-inbox-feed`'s "Source 2" derivation: query
 * `messages.inboxForUser` (DMs / @mentions / notify=all channel
 * messages, already excluding self-authored + system/call kinds and
 * channel-membership-gated server-side), then drop anything at or below
 * the channel's `last_read_at` so already-read messages don't surface.
 * `useLastReadMap` falls back to `joined_at` when `last_read_at` is null,
 * so freshly-joined channels don't flood the inbox with prior history.
 *
 * The shaped result is what both the banner/sound hook and the badge
 * count consume — keeping the two surfaces in agreement (they used to
 * disagree: the feed read `messages`, the badge read the starved
 * `notification_events`).
 */
export function useMessageInboxItems(): MessageInboxItem[] {
  const { user } = useCurrentUser();
  const [messagesInboxRaw] = useQuery(
    user ? queries.messages.inboxForUser() : null,
  );
  const [lastReadAtByChannel] = useLastReadMap();

  return useMemo(() => {
    if (!user || !messagesInboxRaw || messagesInboxRaw.length === 0) {
      return EMPTY_MESSAGE_INBOX_ITEMS;
    }
    const currentUserId = user.id;
    const items: MessageInboxItem[] = [];
    for (const m of messagesInboxRaw) {
      // Defensive: query already filters `user_id != sub`, but never
      // banner the user about their own message even if the row slips
      // through (e.g. a server backfill edge case).
      if (m.user_id === currentUserId) continue;

      // Unread filter: only surface messages newer than the user's
      // last_read_at for that channel (mirrors `use-inbox-feed`).
      const lastRead = lastReadAtByChannel.get(m.channel_id);
      if (lastRead != null && m.created_at <= lastRead) continue;

      const channelType = m.channel?.type ?? null;
      const mentionsArr = Array.isArray(m.message_mentions)
        ? m.message_mentions.map((mm) => mm.user_id)
        : null;
      const reason =
        channelType === "dm" || channelType === "group_dm"
          ? "dm"
          : mentionsArr && mentionsArr.includes(currentUserId)
            ? "mention"
            : "channel";

      const senderName =
        (m.sender?.display_name as string | undefined) ??
        m.sender_display_name ??
        "New message";

      items.push({
        id: m.id,
        channelId: m.channel_id,
        messageId: m.id,
        threadId: m.thread_id ?? undefined,
        workspaceId: m.channel?.workspace_id ?? undefined,
        reason,
        title: senderName,
        body: m.preview_text ?? m.content ?? "",
        actorUserId: m.user_id,
        createdAt: m.created_at,
      });
    }
    return items.length > 0 ? items : EMPTY_MESSAGE_INBOX_ITEMS;
  }, [user, messagesInboxRaw, lastReadAtByChannel]);
}

/**
 * Inbox badge count (total unread message-derived inbox items).
 *
 * Repointed (slice 1 fix) off the starved `notification_events`
 * publication onto `messages.inboxForUser` via `useMessageInboxItems`,
 * so the badge agrees with the inbox feed and the OS banners. The
 * previous source replicated only admin reasons after migration 104, so
 * the badge undercounted every DM / mention / channel message.
 *
 * Muted-channel guard (slice 1 follow-up): pass-1 added a muted-channel
 * filter to the OS banner (`useNotifications` skips a muted item whose
 * reason isn't a direct mention) but NOT to this badge — so the badge
 * counted muted-channel non-mention messages the banner suppressed, and
 * diverged from the old `notification_events`-backed badge that excluded
 * them. We re-apply the EXACT banner predicate here — exclude items
 * where the channel is muted AND the reason isn't a direct mention (a
 * muted DM is suppressed too; only a direct @mention pierces the mute).
 * The filter lives here and NOT in `useMessageInboxItems`, so the shared
 * inbox-item list stays intact for any other consumer (e.g. the inbox
 * feed/list, which surfaces muted items so they're still reachable).
 */
export function useInboxBadgeCount() {
  const items = useMessageInboxItems();
  const [mutedSet] = useMutedSet();
  const count = useMemo(() => {
    let n = 0;
    for (const item of items) {
      // Banner-exact predicate: a muted non-mention is suppressed.
      if (mutedSet.has(item.channelId) && item.reason !== "mention") continue;
      n++;
    }
    return n;
  }, [items, mutedSet]);
  return useMemo(() => [count] as const, [count]);
}
